import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage } = useLanguage();

  const navLinks = [
    { href: '/', labelKey: 'nav.home' },
    { href: '/team', labelKey: 'nav.team' },
    { href: '/the-home', labelKey: 'nav.theSetting' },
    { href: '/the-field', labelKey: 'nav.theField' },
    { href: '/contact', labelKey: 'nav.contact' },
  ];

  const languages = ['EN', 'FR', 'PT'];

  const isActive = (href) => location.pathname === href;

  return (
    <header
      data-testid="header"
      className="fixed top-0 left-0 right-0 z-50 bg-[#FCF7EC] border-b border-[#1E4748]/10"
    >
      <div className="max-w-6xl mx-auto px-6">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="logo-link"
            className="flex items-center"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_mindful-container/artifacts/4pue4la0_LOGO_01.png"
              alt="CONTAINER"
              className="h-20 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${t(link.labelKey, 'en').toLowerCase().replace(' ', '-')}`}
                className={`nav-link text-sm font-sans tracking-wide ${
                  isActive(link.href) ? 'text-spruce font-medium' : ''
                }`}
              >
                {t(link.labelKey, language)}
              </Link>
            ))}
          </div>

          {/* Language Selector + CTA */}
          <div className="hidden md:flex items-center gap-4">
            <div className="flex items-center gap-1 text-xs font-sans" data-testid="language-selector">
              {languages.map((lang, i) => (
                <React.Fragment key={lang}>
                  <button
                    onClick={() => setLanguage(lang.toLowerCase())}
                    data-testid={`lang-${lang.toLowerCase()}`}
                    className={`px-1 py-0.5 tracking-wide transition-colors ${
                      language === lang.toLowerCase()
                        ? 'text-spruce font-semibold'
                        : 'text-gray-400 hover:text-deep-teal'
                    }`}
                  >
                    {lang}
                  </button>
                  {i < languages.length - 1 && <span className="text-gray-300">|</span>}
                </React.Fragment>
              ))}
            </div>

            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="btn-primary px-6 py-2.5 font-sans text-sm tracking-wide rounded-full"
            >
              {t('nav.bookCall', language)}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="md:hidden p-2 text-spruce"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#FCF7EC] border-t border-[#1E4748]/10"
        >
          <div className="px-6 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`mobile-nav-${t(link.labelKey, 'en').toLowerCase().replace(' ', '-')}`}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block text-deep-teal font-sans text-lg ${
                  isActive(link.href) ? 'text-spruce font-medium' : ''
                }`}
              >
                {t(link.labelKey, language)}
              </Link>
            ))}

            {/* Mobile Language Selector */}
            <div className="flex items-center gap-2 pt-2">
              {languages.map((lang, i) => (
                <React.Fragment key={lang}>
                  <button
                    onClick={() => setLanguage(lang.toLowerCase())}
                    className={`text-sm font-sans tracking-wide ${
                      language === lang.toLowerCase()
                        ? 'text-spruce font-semibold'
                        : 'text-gray-400'
                    }`}
                  >
                    {lang}
                  </button>
                  {i < languages.length - 1 && <span className="text-gray-300">|</span>}
                </React.Fragment>
              ))}
            </div>

            <Link
              to="/contact"
              data-testid="mobile-nav-cta"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block btn-primary px-6 py-3 font-sans text-center rounded-full mt-4"
            >
              {t('nav.bookCall', language)}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
